/**
 * Sawyer E4 (#754) — Team messages client.
 *
 * Typed calls to `/api/v2/admin/staff-messages/*`, with every response
 * parsed by Zod before the UI sees it (parse, don't validate). Kept out of
 * the 11k-line `lib/api.ts` so other lanes' edits there cannot conflict.
 *
 * Two transports, on purpose:
 *  - page calls go through the shared `request` helper with NO retries (a
 *    failed send must surface at once as "Not sent", not after three
 *    backoffs), so a 401 still signs a dead session out the normal way;
 *  - the shell's availability probe and its 30s unread poll use a raw
 *    `fetch` that never rejects and never triggers the 401 auto-logout
 *    (`probeOwnerSettingsAvailable`'s contract): a background poll must not
 *    sign a working staffer out, and every failure reads as "no data".
 */
import { z } from 'zod';
import { request, readSession } from './api.js';

export const BASE = '/api/v2/admin/staff-messages';
export const MAX_BODY = 2000;

const person = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  isActive: z.boolean(),
});
const dogTag = z.object({ id: z.string(), name: z.string(), breed: z.string().nullable() });
const postView = z.object({
  id: z.string(),
  body: z.string(),
  createdAt: z.string(),
  author: person,
  dog: dogTag.nullable(),
  isMine: z.boolean(),
});
const messageView = z.object({
  id: z.string(),
  threadId: z.string(),
  body: z.string(),
  createdAt: z.string(),
  senderId: z.string(),
  isMine: z.boolean(),
});
const threadView = z.object({
  id: z.string(),
  other: person,
  lastMessage: z
    .object({ preview: z.string(), createdAt: z.string(), fromMe: z.boolean() })
    .nullable(),
  unreadCount: z.number().int().nonnegative(),
});

export const unreadSchema = z.object({
  board: z.number().int().nonnegative(),
  direct: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export const boardSchema = z.object({
  posts: z.array(postView),
  lastReadAt: z.string().nullable(),
  nextCursor: z.string().nullable(),
});
const postResponse = z.object({ post: postView });
const peopleSchema = z.object({ people: z.array(person) });
const dogsSchema = z.object({ dogs: z.array(dogTag) });
const threadsSchema = z.object({ threads: z.array(threadView) });
const startResponse = z.object({ thread: threadView, message: messageView });
const threadSchema = z.object({
  thread: threadView,
  messages: z.array(messageView),
  nextCursor: z.string().nullable(),
});
const sendResponse = z.object({ message: messageView });

export type UnreadCounts = z.infer<typeof unreadSchema>;
export type Person = z.infer<typeof person>;
export type DogTag = z.infer<typeof dogTag>;
export type Post = z.infer<typeof postView>;
export type Message = z.infer<typeof messageView>;
export type Thread = z.infer<typeof threadView>;
export type BoardPage = z.infer<typeof boardSchema>;
export type ThreadPage = z.infer<typeof threadSchema>;

/** A call either parsed, or failed with the server's machine code (if any). */
export type Outcome<T> = { ok: true; data: T } | { ok: false; code: string | null };

async function call<T>(
  apiUrl: string,
  endpoint: string,
  schema: z.ZodType<T>,
  init: RequestInit,
): Promise<Outcome<T>> {
  const res = await request<unknown>(apiUrl, endpoint, init, { maxRetries: 0 });
  if (res.error !== undefined) return { ok: false, code: res.code ?? null };
  const parsed = schema.safeParse(res.data);
  return parsed.success ? { ok: true, data: parsed.data } : { ok: false, code: 'malformed' };
}

/**
 * Mark-read answers 204 (no body), which the shared `request` helper reads
 * as "unexpected response", so these two go out on a quiet raw fetch. A
 * failed mark is harmless: the next open marks again.
 */
async function markQuiet(apiUrl: string, endpoint: string, body: unknown): Promise<boolean> {
  const { token } = readSession();
  if (!token) return false;
  try {
    const res = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    return res.status === 204;
  } catch {
    return false;
  }
}

const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const GET: RequestInit = { method: 'GET' };

export const staffMessagesApi = {
  board: (apiUrl: string, before?: string) =>
    call(apiUrl, `${BASE}/board${before ? `?before=${encodeURIComponent(before)}` : ''}`, boardSchema, GET),
  postBoard: (apiUrl: string, body: string, dogId: string | null) =>
    call(apiUrl, `${BASE}/board`, postResponse, post({ body, dogId })),
  markBoardRead: (apiUrl: string, upTo?: string) =>
    markQuiet(apiUrl, `${BASE}/board/read`, upTo ? { upTo } : {}),
  people: (apiUrl: string) => call(apiUrl, `${BASE}/people`, peopleSchema, GET),
  dogs: (apiUrl: string, q: string) =>
    call(apiUrl, `${BASE}/dogs${q ? `?q=${encodeURIComponent(q)}` : ''}`, dogsSchema, GET),
  threads: (apiUrl: string) => call(apiUrl, `${BASE}/direct`, threadsSchema, GET),
  startDirect: (apiUrl: string, recipientStaffId: string, body: string) =>
    call(apiUrl, `${BASE}/direct`, startResponse, post({ recipientStaffId, body })),
  thread: (apiUrl: string, threadId: string) =>
    call(apiUrl, `${BASE}/direct/${encodeURIComponent(threadId)}`, threadSchema, GET),
  send: (apiUrl: string, threadId: string, body: string) =>
    call(apiUrl, `${BASE}/direct/${encodeURIComponent(threadId)}/messages`, sendResponse, post({ body })),
  markThreadRead: (apiUrl: string, threadId: string, upTo?: string) =>
    markQuiet(apiUrl, `${BASE}/direct/${encodeURIComponent(threadId)}/read`, upTo ? { upTo } : {}),
};

/**
 * The unread count, quietly: a parsed 200 or `null`. Never rejects, never
 * signs anyone out, never logs. Used by the availability probe (whose 200
 * body seeds the badge, so the shell makes one request on mount, not two)
 * and by the 30s poll (MAC1: a 500, a timeout or a malformed body is "no
 * data", and the badge keeps its last good value).
 */
export async function fetchUnreadQuiet(apiUrl: string, token?: string | null): Promise<UnreadCounts | null> {
  const bearer = token ?? readSession().token;
  if (!bearer) return null;
  try {
    const res = await fetch(`${apiUrl}${BASE}/unread`, { headers: { Authorization: `Bearer ${bearer}` } });
    if (res.status !== 200) return null;
    const parsed = unreadSchema.safeParse(await res.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Does this facility's SERVER carry Team messages yet? A 200 from
 * `GET /unread` means yes; a pre-deploy server's 403 `route_not_mapped`, a
 * 404, a 5xx or a network failure all mean no (fail closed, AC17).
 */
export async function probeStaffMessagesAvailable(
  apiUrl: string,
  token: string,
): Promise<{ available: boolean; counts: UnreadCounts | null }> {
  const counts = await fetchUnreadQuiet(apiUrl, token);
  return { available: counts !== null, counts };
}

/** English role labels for the people list and post headers. */
const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  receptionist: 'Receptionist',
  walker: 'Walker',
  groomer: 'Groomer',
  kennel_tech: 'Kennel tech',
  accountant: 'Accountant',
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? '';
}

export function personName(p: Pick<Person, 'firstName' | 'lastName'>): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

/** "just now", "12 min ago", "3 h ago", then a short date. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now.getTime() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function absoluteTime(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t)
    ? ''
    : new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** The sentence a staffer sees for a failure; never a code or a status. */
export const LOAD_ERROR = "Couldn't load team messages. Check your connection and try again.";
